import { AlertTriangleIcon } from 'lucide-react';
import { Dispatch, ReactElement, SetStateAction, useCallback, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@/design-system/components/ui';
import { cn } from '@/design-system/lib/utils';

const DIALOG_CLOSE_ANIMATION_MS = 200;

type ConfirmType = 'error' | 'warning' | 'info' | 'success';

interface UseConfirmPromise {
  resolve: (value: boolean) => void;
  title: string;
  message: string | ReactElement;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

interface ConfirmParams {
  title: string;
  message: string | ReactElement;
  confirmButtonText?: string;
  cancelButtonText?: string;
  initialConfirmDisabled?: boolean;
}

type UseConfirmReturn = [ReactElement, (params: ConfirmParams) => Promise<boolean>, Dispatch<SetStateAction<boolean>>];

const typeStyles: Record<ConfirmType, { bg: string; text: string }> = {
  warning: { bg: 'tw:bg-amber-100', text: 'tw:text-amber-600' },
  error: { bg: 'tw:bg-red-100', text: 'tw:text-red-600' },
  info: { bg: 'tw:bg-blue-100', text: 'tw:text-blue-600' },
  success: { bg: 'tw:bg-green-100', text: 'tw:text-green-600' },
};

export function useConfirm(type: ConfirmType | undefined = 'warning'): UseConfirmReturn {
  const [promise, setPromise] = useState<UseConfirmPromise | null>(null);
  const [confirmDisabled, setConfirmDisabled] = useState(false);
  const [open, setOpen] = useState(false);

  const confirm = useCallback(
    ({
      title,
      message,
      confirmButtonText,
      cancelButtonText,
      initialConfirmDisabled = false,
    }: ConfirmParams): Promise<boolean> =>
      new Promise((resolve) => {
        setConfirmDisabled(initialConfirmDisabled);
        setPromise({ resolve, title, message, confirmButtonText, cancelButtonText });
        setOpen(true);
      }),
    [],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    // Clear promise state after close animation completes
    setTimeout(() => setPromise(null), DIALOG_CLOSE_ANIMATION_MS);
  }, []);

  const handleConfirm = useCallback(() => {
    promise?.resolve(true);
    handleClose();
  }, [promise, handleClose]);

  const handleCancel = useCallback(() => {
    promise?.resolve(false);
    handleClose();
  }, [promise, handleClose]);

  const styles = typeStyles[type ?? 'warning'];

  const ConfirmationDialog = (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="tw:flex tw:items-start tw:gap-3">
            <div
              className={cn(
                'tw:flex tw:h-10 tw:w-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full',
                styles.bg,
              )}
            >
              <AlertTriangleIcon className={cn('tw:h-5 tw:w-5', styles.text)} />
            </div>
            <div className="tw:flex tw:flex-col tw:gap-1">
              <AlertDialogTitle>{promise?.title}</AlertDialogTitle>
              {promise?.message &&
                (typeof promise.message === 'string' ? (
                  <AlertDialogDescription>{promise.message}</AlertDialogDescription>
                ) : (
                  <div className="tw:text-sm tw:text-gray-500">{promise.message}</div>
                ))}
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button variant="ghost" onClick={handleCancel}>
              {promise?.cancelButtonText || 'No'}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant={type === 'error' ? 'destructive' : 'primary'}
              onClick={handleConfirm}
              disabled={confirmDisabled}
            >
              {promise?.confirmButtonText || 'Yes'}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return [ConfirmationDialog, confirm, setConfirmDisabled];
}

export default useConfirm;
